import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, LogOut, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import client, { auth } from "@/lib/api";
import { AnalyticsModal } from "@/components/admin/AnalyticsModal";
import { TwoFactorModal } from "@/components/admin/TwoFactorModal";
import { QuoteRow } from "@/components/admin/QuoteRow";
import { BookingRow } from "@/components/admin/BookingRow";
import { BookingDetailsModal } from "@/components/admin/BookingDetailsModal";

const TABLE_HEADERS = ["Couple", "Email", "Status", "Engagement", "Created"];

export default function AdminDashboard() {
  const [quotes, setQuotes] = useState([]);
  const [summary, setSummary] = useState({});
  const [analyticsQuote, setAnalyticsQuote] = useState(null);
  const [detailsQuote, setDetailsQuote] = useState(null);
  const [tab, setTab] = useState("quotes");
  const [show2FA, setShow2FA] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.get("/quotes");
      setQuotes(r.data.quotes || []);
      const s = await client.get("/quotes-analytics/summary");
      setSummary(s.data.summary || {});
    } catch (err) {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const s = await client.get("/quotes-analytics/summary");
        setSummary(s.data.summary || {});
      } catch {}
    }, 30000);
    return () => clearInterval(t);
  }, []);

  async function onSend(q) {
    if (!window.confirm(`Send this quote to ${q.client_email} via Brevo?`)) return;
    setSendingId(q.id);
    try {
      await client.post(`/quotes/${q.id}/send`);
      toast.success("Quote sent");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSendingId(null);
    }
  }

  async function onDelete(q) {
    if (!window.confirm(`Delete quote for ${q.partner1_name} & ${q.partner2_name}?`)) return;
    try {
      await client.delete(`/quotes/${q.id}`);
      toast.success("Quote deleted");
      load();
    } catch (err) {
      toast.error("Failed to delete");
    }
  }

  async function onReactivate(q) {
    try {
      await client.post(`/quotes/${q.id}/reactivate`);
      toast.success("Quote reactivated, 7-day hold restarted");
      load();
    } catch {
      toast.error("Failed to reactivate");
    }
  }

  async function onWhatsApp(q) {
    const win = window.open("", "_blank");
    try {
      const r = await client.get("/brevo/contact-lookup", {
        params: { email: q.client_email },
      });
      const mobile = r.data.found ? r.data.mobile : null;
      if (!mobile) {
        win.close();
        toast.error("No mobile number on their Brevo contact");
        return;
      }
      win.location = `https://wa.me/${String(mobile).replace(/\D/g, "")}`;
    } catch {
      win.close();
      toast.error("Brevo lookup failed");
    }
  }

  async function signOut() {
    try {
      await client.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed; clearing local session anyway", err);
    }
    auth.clearAdmin();
    nav("/admin/login");
  }

  const booked = quotes
    .filter((q) => q.status === "accepted")
    .sort((a, b) => String(b.acceptance?.accepted_at || "").localeCompare(String(a.acceptance?.accepted_at || "")));
  const pipeline = quotes.filter((q) => q.status !== "accepted");

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-dashboard">
      <header className="border-b border-[#E5E3DB] bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="px-6 md:px-12 lg:px-16 py-5 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <div>
              <div className="text-[10px] tracking-overline uppercase text-[#8A8782] font-medium">
                Wedding Video Essex
              </div>
              <div className="font-display text-xl leading-none tracking-tight text-[#2F2E2E]">
                Admin
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/quotes/new"
              data-testid="new-quote-btn"
              className="inline-flex items-center gap-2 bg-[#2F2E2E] text-white px-5 py-3 text-xs tracking-overline uppercase font-medium hover:bg-[#454341] transition-colors"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              New Quote
            </Link>
            <button
              onClick={() => setShow2FA(true)}
              data-testid="twofactor-open-btn"
              className="inline-flex items-center gap-2 text-[#5C5A56] hover:text-[#1F1E1D] px-4 py-3 text-xs tracking-overline uppercase font-medium transition-colors"
              title="Two-factor login settings"
            >
              <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
              2FA
            </button>
            <button
              onClick={signOut}
              data-testid="sign-out-btn"
              className="inline-flex items-center gap-2 text-[#5C5A56] hover:text-[#1F1E1D] px-4 py-3 text-xs tracking-overline uppercase font-medium transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-12 lg:px-16 py-12">
        <div className="mb-10 fade-up">
          <div className="text-xs tracking-overline uppercase text-[#A9927D] mb-3 font-medium">
            Dashboard
          </div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-none text-[#1F1E1D]">
            {tab === "booked" ? "Booked" : "Quotes"}
          </h1>
          <p className="mt-4 text-[#5C5A56] max-w-2xl">
            {tab === "booked"
              ? "Every couple who has accepted their quote and secured their date."
              : "Create, preview, and send personalised quotes to couples."}
          </p>
          <div className="mt-8 inline-flex border border-[#D1CEC2] bg-white" data-testid="dashboard-tabs">
            <button
              onClick={() => setTab("quotes")}
              data-testid="tab-quotes"
              className={`px-6 py-3 text-xs tracking-overline uppercase font-medium transition-colors ${
                tab === "quotes" ? "bg-[#2F2E2E] text-white" : "text-[#5C5A56] hover:text-[#1F1E1D]"
              }`}
            >
              Quotes {pipeline.length > 0 && `(${pipeline.length})`}
            </button>
            <button
              onClick={() => setTab("booked")}
              data-testid="tab-booked"
              className={`px-6 py-3 text-xs tracking-overline uppercase font-medium transition-colors ${
                tab === "booked" ? "bg-[#2F2E2E] text-white" : "text-[#5C5A56] hover:text-[#1F1E1D]"
              }`}
            >
              Booked {booked.length > 0 && `(${booked.length})`}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#5C5A56]">
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
            Loading quotes…
          </div>
        ) : tab === "booked" ? (
          booked.length === 0 ? (
            <div
              data-testid="empty-bookings"
              className="border border-dashed border-[#D1CEC2] bg-white p-16 text-center fade-up"
            >
              <div className="font-display text-3xl mb-3 text-[#2F2E2E]">
                No bookings yet.
              </div>
              <p className="text-[#5C5A56]">
                When a couple accepts their quote, they'll appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4 fade-up" data-testid="bookings-list">
              {booked.map((q) => (
                <BookingRow
                  key={q.id}
                  q={q}
                  onDetails={setDetailsQuote}
                  onAnalytics={setAnalyticsQuote}
                  onWhatsApp={onWhatsApp}
                />
              ))}
            </div>
          )
        ) : pipeline.length === 0 ? (
          <div
            data-testid="empty-quotes"
            className="border border-dashed border-[#D1CEC2] bg-white p-16 text-center fade-up"
          >
            <div className="font-display text-3xl mb-3 text-[#2F2E2E]">
              No quotes yet.
            </div>
            <p className="text-[#5C5A56] mb-8">
              Start by creating your first quote for a couple.
            </p>
            <Link
              to="/admin/quotes/new"
              className="inline-flex items-center gap-2 bg-[#2F2E2E] text-white px-6 py-3 text-xs tracking-overline uppercase font-medium hover:bg-[#454341] transition-colors"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              Create first quote
            </Link>
          </div>
        ) : (
          <div
            className="bg-white border border-[#E5E3DB] overflow-x-auto fade-up"
            data-testid="quotes-table"
          >
            <table className="w-full">
              <thead className="border-b border-[#E5E3DB]">
                <tr className="text-left">
                  {TABLE_HEADERS.map((h) => (
                    <th
                      key={h}
                      className="px-6 py-4 text-[10px] tracking-overline uppercase font-medium text-[#8A8782]"
                    >
                      {h}
                    </th>
                  ))}
                  <th className="px-6 py-4 text-right text-[10px] tracking-overline uppercase font-medium text-[#8A8782]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {pipeline.map((q) => (
                  <QuoteRow
                    key={q.id}
                    q={q}
                    stats={summary[q.id]}
                    sendingId={sendingId}
                    onSend={onSend}
                    onDelete={onDelete}
                    onAnalytics={setAnalyticsQuote}
                    onReactivate={onReactivate}
                    onWhatsApp={onWhatsApp}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {analyticsQuote && (
        <AnalyticsModal quote={analyticsQuote} onClose={() => setAnalyticsQuote(null)} />
      )}
      {detailsQuote && (
        <BookingDetailsModal quote={detailsQuote} onClose={() => setDetailsQuote(null)} />
      )}
      {show2FA && <TwoFactorModal onClose={() => setShow2FA(false)} />}
    </div>
  );
}
